import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Icon, cn, showToast, Toast } from '@evogard/ui';
import { BREAK_REFUSAL_MESSAGES, houseLocalOf } from '@evogard/domain';
import type { BreakCurrent } from '@evogard/contract';
import { api, ok } from '../lib/api.js';
import { useSessionContext } from '../lib/session.js';
import { playNotificationSound } from '../lib/sound.js';

/** O estado do intervalo da pessoa — o aviso de tempo real e a relação o invalidam. */
export const BREAK_CURRENT_KEY = ['intervalo', 'atual'] as const;

/** Quanto antes do horário o lembrete toca — o do legado. */
const REMINDER_BEFORE_MS = 60_000;

/**
 * O botão do intervalo, no topo — ativar dentro do próprio horário, encerrar
 * quando voltar, e a contagem regressiva no meio.
 *
 * Portado de `intervalo-toggle.tsx` e `intervalo-lembrete-listener.tsx` do
 * legado. Consulta o estado a cada 20 s e ao voltar o foco da aba; o lembrete
 * toca 1 minuto antes do horário.
 *
 * **Quem não tem horário não vê nada.** O botão desabilitado com "sem horário"
 * apareceria para toda a casa no dia em que o módulo entrasse, e a maioria não
 * tem intervalo atribuído. **E quem tem só o vê perto da hora** — de 1 minuto
 * antes do horário até o fim dele (`useVisibleWindow`).
 *
 * Mora em `components/` porque quem o desenha é o `Header`, que é da moldura —
 * o mesmo motivo do `BalanceWidget`.
 */
export function BreakToggle() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const client = useQueryClient();

  const query = useQuery({
    queryKey: [...BREAK_CURRENT_KEY, tenantId],
    enabled: tenantId !== undefined,
    queryFn: async () => {
      const r = await api.breaks.current();
      const body = ok(r, 200, 'Não foi possível carregar o intervalo.');
      // a diferença entre o relógio do servidor e o desta máquina
      return { ...body, skew: Date.parse(body.now) - Date.now() };
    },
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
  });

  const save = (body: BreakCurrent) =>
    client.setQueryData([...BREAK_CURRENT_KEY, tenantId], {
      ...body,
      skew: Date.parse(body.now) - Date.now(),
    });
  const fail = (error: Error) =>
    showToast((dismiss) => (
      <Toast title="Intervalo" icon="atencao" onClose={dismiss}>
        <p className="text-sm">{error.message}</p>
      </Toast>
    ));

  const activate = useMutation({
    mutationFn: async () =>
      ok(await api.breaks.activate({ body: {} }), 201, 'Não foi possível ativar.'),
    onSuccess: save,
    onError: fail,
  });
  const end = useMutation({
    mutationFn: async (id: number) =>
      ok(
        await api.breaks.end({ params: { id }, body: {} }),
        200,
        'Não foi possível encerrar.',
      ),
    onSuccess: save,
    onError: fail,
  });

  useReminder(query.data);
  const visible = useVisibleWindow(query.data);

  const data = query.data;
  if (!data) return null;
  if (data.mode === 'fixo' && data.slot === null && data.active === null) return null;
  if (!data.active && !visible) return null;

  if (data.active) {
    return (
      <Running
        expectedEndAt={data.active.expectedEndAt}
        skew={data.skew}
        busy={end.isPending}
        onEnd={() => end.mutate(data.active!.id)}
      />
    );
  }

  const disabled = data.refusal !== null || activate.isPending;
  /* o líder começa a qualquer hora da janela em que caibam os 15 minutos */
  const free = data.mode === 'livre';
  const when = free
    ? `de ${houseLocalOf(data.slotStartsAt!).slice(11)} até ${houseLocalOf(data.lastStartAt!).slice(11)}`
    : data.slot;
  const hint =
    data.refusal === null
      ? free
        ? `Intervalo livre: comece ${when}.`
        : `Seu horário: ${when}`
      : `${BREAK_REFUSAL_MESSAGES[data.refusal]} ${free ? `Comece ${when}.` : `Seu horário: ${when}.`}`;
  const label = data.usedToday
    ? 'Intervalo utilizado'
    : free
      ? 'Intervalo livre'
      : `Intervalo ${data.slot}`;

  return (
    <Button
      size="sm"
      variant={disabled ? 'secundario' : 'primario'}
      disabled={disabled}
      loading={activate.isPending}
      icon="relogio"
      onClick={() => activate.mutate()}
      title={hint}
      aria-label={`Ativar intervalo. ${hint}`}
    >
      <span className="hidden sm:inline">{label}</span>
    </Button>
  );
}

/** O intervalo aberto: a contagem, que vira "Tempo esgotado" e fica vermelha. */
function Running({
  expectedEndAt,
  skew,
  busy,
  onEnd,
}: {
  expectedEndAt: string;
  skew: number;
  busy: boolean;
  onEnd: () => void;
}) {
  const [now, setNow] = useState(() => Date.now() + skew);
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now() + skew), 1_000);
    return () => window.clearInterval(id);
  }, [skew]);

  const left = Date.parse(expectedEndAt) - now;
  const over = left <= 0;
  const seconds = Math.max(0, Math.ceil(left / 1_000));
  const clock = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

  return (
    <div
      role="status"
      className={cn(
        'flex h-11 shrink-0 items-center gap-2 rounded-borda border-2 pl-3 pr-1 lg:h-[2.125rem]',
        over ? 'border-erro bg-erro-suave text-erro' : 'border-sucesso bg-sucesso-suave text-sucesso',
      )}
    >
      <Icon nome="relogio" />
      <span className="whitespace-nowrap text-sm font-semibold tabular-nums">
        {over ? 'Tempo esgotado' : clock}
      </span>
      <Button size="sm" variant="secundario" loading={busy} onClick={onEnd}>
        Encerrar
      </Button>
    </div>
  );
}

/**
 * Se o botão aparece AGORA: de 1 minuto antes do horário — o mesmo instante do
 * lembrete — até o fim dele. No `livre`, de 1 minuto antes da janela até o
 * último instante em que ainda cabem os 15 minutos.
 *
 * Fora disso o botão some: desabilitado o dia inteiro, ele ocupava o topo de
 * quem não estava perto do intervalo. O intervalo ABERTO não passa por aqui —
 * ele aparece sempre, até ser encerrado.
 *
 * A consulta é de 20 em 20 s, e o botão não pode esperar por ela para
 * aparecer junto com o lembrete: um temporizador acorda a tela na próxima
 * fronteira.
 */
function useVisibleWindow(data: (BreakCurrent & { skew: number }) | undefined): boolean {
  const opensAt = data?.slotStartsAt ? Date.parse(data.slotStartsAt) - REMINDER_BEFORE_MS : null;
  const closesAt =
    data?.mode === 'livre'
      ? data.lastStartAt
        ? Date.parse(data.lastStartAt)
        : null
      : data?.slotEndsAt
        ? Date.parse(data.slotEndsAt)
        : null;
  const skew = data?.skew ?? 0;
  const [tick, wake] = useState(0);

  useEffect(() => {
    if (opensAt === null || closesAt === null) return;
    const now = Date.now() + skew;
    const next = now < opensAt ? opensAt : now < closesAt ? closesAt : null;
    if (next === null) return;
    const id = window.setTimeout(() => wake((n) => n + 1), next - now + 50);
    return () => window.clearTimeout(id);
  }, [opensAt, closesAt, skew, tick]);

  const now = Date.now() + skew;
  return opensAt !== null && closesAt !== null && now >= opensAt && now < closesAt;
}

/**
 * Toca o som 1 minuto antes do horário — três vezes, como o legado. O som é o
 * mesmo dos avisos em tempo real (`notification.mp3`): o legado tinha um
 * arquivo próprio (`intervalbeggins.mp3`) que não veio para cá.
 */
function useReminder(data: (BreakCurrent & { skew: number }) | undefined) {
  const played = useRef<string | null>(null);
  // o intervalo livre do líder não tem horário marcado: não há o que lembrar
  const startsAt = data?.mode === 'fixo' ? (data.slotStartsAt ?? null) : null;
  const pending = data !== undefined && !data.usedToday && data.active === null;
  const skew = data?.skew ?? 0;

  useEffect(() => {
    if (!startsAt || !pending || played.current === startsAt) return;
    const wait = Date.parse(startsAt) - REMINDER_BEFORE_MS - (Date.now() + skew);
    // passou do lembrete: não toca atrasado
    if (wait < -REMINDER_BEFORE_MS) return;
    const timers: number[] = [];
    timers.push(
      window.setTimeout(
        () => {
          played.current = startsAt;
          for (let i = 0; i < 3; i++) {
            timers.push(window.setTimeout(playNotificationSound, i * 1_500));
          }
          showToast((dismiss) => (
            <Toast title="Seu intervalo começa em 1 minuto" icon="relogio" onClose={dismiss}>
              <p className="text-sm">
                Horário: {houseLocalOf(startsAt).slice(11)}. Ative o intervalo quando sair.
              </p>
            </Toast>
          ));
        },
        Math.max(0, wait),
      ),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [startsAt, pending, skew]);
}
